'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

// Deliberately null rather than {} — an empty object is truthy, so a missing
// provider used to slip through the guard below and leave signIn/signUp
// undefined, which failed silently at the point of use.
const AuthContext = createContext<any>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  // Your own profile comes from my_profile(), not a select on the table.
  // profiles.phone / profiles.email are no longer readable column-by-column —
  // they only leave the database through a metered reveal — so `select *` here
  // would now be refused outright.
  const fetchProfile = async (_userId: string) => {
    try {
      const { data, error } = await supabase.rpc('my_profile');
      const row = Array.isArray(data) ? data[0] : data;
      if (!error && row) setProfile(row);
    } catch {}
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      setLoading(false);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else setProfile(null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up — saves full profile details
  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata.fullName || '',
          business_name: metadata.businessName || '',
          business_type: metadata.businessType || '',
          phone: metadata.phone || '',
          county: metadata.county || '',
          postcode: metadata.postcode || '',
          cph_number: metadata.cphNumber || '',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`,
      }
    });
    if (error) throw error;

    // The row itself is created by the ensure_profile_on_signup trigger, which
    // only captures name, email, business name and phone. This fills in the rest
    // of the sign-up form.
    //
    // Deliberately an update, not an upsert: an upsert sends `id` and
    // `membership_tier`, and neither is writable by a member any more — a member
    // could otherwise set their own plan to booster. An upsert would just 403 and
    // quietly lose the county, postcode and CPH number the member typed in.
    if (data.user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          email,
          full_name: metadata.fullName || '',
          business_name: metadata.businessName || '',
          business_type: metadata.businessType || null,
          phone: metadata.phone || '',
          county: metadata.county || '',
          postcode: metadata.postcode || '',
          cph_number: metadata.cphNumber || '',
        })
        .eq('id', data.user.id);
      // Not fatal — the account exists and they can fill this in on their
      // profile page — but it should not fail silently either.
      if (profileError) {
        console.error('Could not save sign-up details to profile:', profileError.message);
      }
    }

    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.user) await fetchProfile(data.user.id);
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at != null;
  };

  // Check if user is admin
  const isAdmin = () => {
    return profile?.is_admin === true;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase.rpc('my_profile');
    if (error) throw error;
    return Array.isArray(data) ? data[0] : data;
  };

  /** Re-read the profile after the member edits it, so the page reflects it. */
  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const value = {
    user,
    session,
    profile,
    loading,
    refreshProfile,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    isAdmin,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
